// Global Instructions Rule Applied!

import { ContactRecord, ContactRecordFields } from '../../../state_manager/data_models/contact_record.ts'
import { ContactAccountsModel } from '../../../state_manager/data_models/contact_accounts_model.ts'
import {
  CompanyTeamEntrySchema,
  CompanyTeamEntryFields
} from '../../../state_manager/data_models/company_team_entry.ts'
import {
  ACTION_TYPE_INSERT,
  FIELD_KEY_PROG,
  FIELD_KEY_ACTION,
  TABLE_CONTACT_ACCOUNTS
} from '../../../state_manager/globals.ts'

const PROG_ACCOUNT_ID = `${FIELD_KEY_PROG}account_id`
const PROG_ACCOUNT_LABEL = `${FIELD_KEY_PROG}account__label`

/**
 * Form data transformation utilities for Contacts
 * Handles conversion between form data and API format
 */

/**
 * Transforms form data to ContactRecord format for API submission
 * Handles nested arrays (prog__current_roles, prog__prior_roles, prog__founder_of, prog__introductions)
 * @param formData - Form data from the contact form
 * @param contactId - The ID of the contact being edited (optional, will use formData if not provided)
 */
export function transformFormDataToContactRecord(
  formData: Record<string, any>,
  contactId?: string | null
): ContactRecord {
  const transformed: Record<string, any> = {
    ...formData
  }

  // Get contact ID from formData or parameter
  const currentContactId = contactId || formData[ContactRecordFields.contacts__id] || null

  // Ensure nested arrays are properly formatted
  if (formData.prog__current_roles) {
    transformed.prog__current_roles = Array.isArray(formData.prog__current_roles)
      ? formData.prog__current_roles.map((role: any) => transformContactAccountRole(role))
      : []
  } else {
    transformed.prog__current_roles = []
  }

  if (formData.prog__prior_roles) {
    transformed.prog__prior_roles = Array.isArray(formData.prog__prior_roles)
      ? formData.prog__prior_roles.map((role: any) => transformContactAccountRole(role))
      : []
  } else {
    transformed.prog__prior_roles = []
  }

  if (formData.prog__founder_of) {
    transformed.prog__founder_of = Array.isArray(formData.prog__founder_of)
      ? formData.prog__founder_of
          .map((item: any) => {
            const entry = transformCompanySelectToCompanyTeamEntry(item, true, currentContactId)
            // Remove temporary fields that aren't part of CompanyTeamEntrySchema
            if (entry) {
              const { account_id, _companyId, _companyLabel, ...cleanEntry } = entry
              return cleanEntry
            }
            return null
          })
          .filter((item: any) => item !== null)
      : []
  } else {
    transformed.prog__founder_of = []
  }

  if (formData.prog__introductions) {
    transformed.prog__introductions = Array.isArray(formData.prog__introductions)
      ? formData.prog__introductions
          .map((item: any) => {
            const entry = transformCompanySelectToCompanyTeamEntry(item, false, currentContactId)
            // Remove temporary fields that aren't part of CompanyTeamEntrySchema
            if (entry) {
              const { account_id, _companyId, _companyLabel, ...cleanEntry } = entry
              return cleanEntry
            }
            return null
          })
          .filter((item: any) => item !== null)
      : []
  } else {
    transformed.prog__introductions = []
  }

  return transformed as ContactRecord
}

/**
 * Transforms a contact account role object to ContactAccountsModel format
 */
function transformContactAccountRole(role: any): ContactAccountsModel {
  return {
    id: role.id || crypto.randomUUID(),
    account_id: role.account_id || null,
    contact_id: role.contact_id || null,
    relationship_category_id: role.relationship_category_id || null,
    title: role.title || null,
    current: role.current ?? true,
    from_date: role.from_date || null,
    to_date: role.to_date || null
  }
}

/**
 * Transforms company select value (from InputManager) to CompanyTeamEntrySchema format
 * @param companySelectValue - Company select value from InputManager (can be {value, label, name} or CompanyTeamEntry)
 * @param isFounder - Whether this is a founder relationship (true for PROG_FOUNDER_OF, false for PROG_INTRODUCTIONS)
 * @param contactId - The ID of the contact being edited (current contact)
 */
function transformCompanySelectToCompanyTeamEntry(
  companySelectValue: any,
  isFounder: boolean,
  contactId?: string | null
): any {
  // If already in CompanyTeamEntrySchema format, clean it and return (but ensure contact ID is set)
  if (companySelectValue && companySelectValue[CompanyTeamEntryFields.contacts__id] !== undefined) {
    // Remove temporary fields that aren't part of CompanyTeamEntrySchema
    const { account_id, _companyId, _companyLabel, ...cleanEntry } = companySelectValue

    // Ensure contact ID is set if provided
    if (contactId && !cleanEntry[CompanyTeamEntryFields.contacts__id]) {
      return {
        ...cleanEntry,
        [CompanyTeamEntryFields.contacts__id]: contactId
      }
    }
    return cleanEntry
  }

  // Extract company ID from company select value
  const companyId = companySelectValue?.value || companySelectValue?.id || null
  if (!companyId) {
    return null
  }

  const PROG_CONTACT_ACCOUNTS_ACTION = `${FIELD_KEY_PROG}${TABLE_CONTACT_ACCOUNTS}${FIELD_KEY_ACTION}`

  return {
    [CompanyTeamEntryFields.contacts__id]: contactId || null, // Current contact ID
    [CompanyTeamEntryFields.contacts__email]: null,
    [CompanyTeamEntryFields.contacts__last_name]: null,
    [CompanyTeamEntryFields.contacts__first_name]: null,
    [CompanyTeamEntryFields.contacts__mobile_phone]: null,
    [CompanyTeamEntryFields.contacts__preferred_name]: null,
    [CompanyTeamEntryFields.contact_accounts__id]: null, // New relationship, no ID yet
    [CompanyTeamEntryFields.contact_accounts__title]: null,
    [CompanyTeamEntryFields.contact_accounts__relationship_category_id]: null,
    [CompanyTeamEntryFields.prog__founder]: isFounder,
    [CompanyTeamEntryFields.contact_accounts__account_id]: companyId, // Store account_id as contact_accounts__account_id (part of schema)
    [CompanyTeamEntryFields.prog__display_name]: null,
    [PROG_CONTACT_ACCOUNTS_ACTION]: ACTION_TYPE_INSERT, // Contact account relationship to be created
    [CompanyTeamEntryFields.prog__contacts__action]: null, // Contact already exists, no action needed
    _companyLabel: companySelectValue?.label || companySelectValue?.name || null
  }
}

/**
 * Transforms CompanyTeamEntrySchema format back to company select format (for InputManager)
 * @param companyTeamEntry - CompanyTeamEntrySchema object
 */
export function transformCompanyTeamEntryToCompanySelect(companyTeamEntry: any): any {
  if (!companyTeamEntry) return null

  // Extract company ID from the entry
  // Check for contact_accounts__account_id (from schema), then fallback to other fields for backward compatibility
  const companyId =
    companyTeamEntry[CompanyTeamEntryFields.contact_accounts__account_id] ||
    companyTeamEntry[PROG_ACCOUNT_ID] ||
    companyTeamEntry.prog__account_id ||
    companyTeamEntry.account_id ||
    companyTeamEntry._companyId ||
    null

  if (!companyId) return null

  // Get company name from prog__account__name (from schema), or fallback to other fields
  const companyName =
    companyTeamEntry[CompanyTeamEntryFields.prog__account__name] ||
    companyTeamEntry.prog__account__name ||
    companyTeamEntry.prog__account__label ||
    companyTeamEntry._companyLabel ||
    companyTeamEntry.account_name ||
    companyTeamEntry.company_name

  // If we have a company name, return object with label
  // If not, return just the companyId string so getCompanyDetailsById can look it up
  if (companyName) {
    return {
      value: companyId,
      label: companyName,
      name: companyName
    }
  }

  // Return just the ID string - InputManager's getCompanyDisplayValue will look it up using getCompanyDetailsById
  return companyId
}

/**
 * Transforms array of CompanyTeamEntrySchema objects to company select format
 */
export function transformCompanyTeamEntriesToCompanySelects(entries: any[]): any[] {
  if (!entries || !Array.isArray(entries)) return []
  return entries.map(transformCompanyTeamEntryToCompanySelect).filter((item) => item !== null)
}
